import { __assign, __extends, __makeTemplateObject, __rest } from "tslib";
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import * as React from 'react';
import RsjfForm from '@rjsf/core';
import styled from 'styled-components';
import { Box } from '../Box';
import { Button } from '../Button';
import * as utils from '../../utils';
import { DescriptionField } from './fields/DescriptionField';
import ObjectField from './fields/ObjectField';
import { TitleField } from './fields/TitleField';
import ArrayFieldTemplate from './templates/ArrayFieldTemplate';
import FieldTemplate from './templates/FieldTemplate';
import ObjectFieldTemplate from './templates/ObjectFieldTemplate';
import BaseInput from './widgets/BaseInput';
import PasswordWidget from './widgets/PasswordWidget';
import SelectWidget from './widgets/SelectWidget';
import TextareaWidget from './widgets/TextareaWidget';
var SUPPORTED_SCHEMA_FORMATS = [
    'data-url',
    'date',
    'date-time',
    'email',
    'hostname',
    'ipv4',
    'ipv6',
    'uri',
];
var KEYWORD_BLACKLIST = ['$schema'];
var widgets = {
    BaseInput: BaseInput,
    SelectWidget: SelectWidget,
    PasswordWidget: PasswordWidget,
    TextareaWidget: TextareaWidget,
};
var fields = {
    DescriptionField: DescriptionField,
    ObjectField: ObjectField,
    TitleField: TitleField,
};
var FormWrapper = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n\tsection {\n\t\tmargin: 0;\n\t\tpadding: 0;\n\t\tborder: 0;\n\t}\n\n\tlabel {\n\t\tfont-size: ", ";\n\t}\n\n\tinput {\n\t\tfont-size: ", ";\n\t}\n\n\t/* Style the error list, since it can't be templated */\n\t.panel-danger,\n\t.error-detail {\n\t\tfont-size: ", ";\n\t\tmargin-top: ", ";\n\t\tmargin-bottom: ", ";\n\t\tlist-style-type: none;\n\t\tpadding-left: 0;\n\t}\n\n\t.text-danger {\n\t\tcolor: ", ";\n\t}\n"], ["\n\tsection {\n\t\tmargin: 0;\n\t\tpadding: 0;\n\t\tborder: 0;\n\t}\n\n\tlabel {\n\t\tfont-size: ", ";\n\t}\n\n\tinput {\n\t\tfont-size: ", ";\n\t}\n\n\t/* Style the error list, since it can't be templated */\n\t.panel-danger,\n\t.error-detail {\n\t\tfont-size: ", ";\n\t\tmargin-top: ", ";\n\t\tmargin-bottom: ", ";\n\t\tlist-style-type: none;\n\t\tpadding-left: 0;\n\t}\n\n\t.text-danger {\n\t\tcolor: ", ";\n\t}\n"])), function (props) { var _a; return utils.px((_a = props === null || props === void 0 ? void 0 : props.theme) === null || _a === void 0 ? void 0 : _a.fontSizes[1]); }, function (props) { var _a; return utils.px((_a = props === null || props === void 0 ? void 0 : props.theme) === null || _a === void 0 ? void 0 : _a.fontSizes[2]); }, function (props) { var _a, _b, _c; return utils.px((_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.theme) === null || _a === void 0 ? void 0 : _a.fontSizes) === null || _b === void 0 ? void 0 : _b[1]) !== null && _c !== void 0 ? _c : 14); }, function (props) { var _a, _b, _c; return utils.px((_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.theme) === null || _a === void 0 ? void 0 : _a.space) === null || _b === void 0 ? void 0 : _b[1]) !== null && _c !== void 0 ? _c : 4); }, function (props) { var _a, _b, _c; return utils.px((_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.theme) === null || _a === void 0 ? void 0 : _a.space) === null || _b === void 0 ? void 0 : _b[1]) !== null && _c !== void 0 ? _c : 4); }, function (props) { var _a, _b, _c, _d; return (_d = (_c = (_b = (_a = props === null || props === void 0 ? void 0 : props.theme) === null || _a === void 0 ? void 0 : _a.colors) === null || _b === void 0 ? void 0 : _b.danger) === null || _c === void 0 ? void 0 : _c.main) !== null && _d !== void 0 ? _d : '#a94442'; });
var parseSchema = function (schema) {
    var whitelist = SUPPORTED_SCHEMA_FORMATS.concat(Object.keys(widgets));
    return utils.stripSchemaFormats(schema, whitelist);
};
var BaseForm = (function (_super) {
    __extends(BaseForm, _super);
    function BaseForm() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.formRef = React.createRef();
        _this.handleKeyUpDown = function (e) {
            if (e.key === 'Enter' && !_this.props.hideSubmitButton) {
                e.stopPropagation();
            }
        };
        _this.change = function (data) {
            _this.setState({ value: data.formData });
            if (_this.props.onFormChange) {
                _this.props.onFormChange(data);
            }
        };
        _this.submit = function () {
            if (_this.formRef.current) {
                _this.formRef.current.submit();
            }
        };
        return _this;
    }
    BaseForm.getDerivedStateFromProps = function (props, state) {
        if (!state) {
            return { schema: parseSchema(props.schema), value: props.value };
        }
        if (!isEqual(state.value, props.value)) {
            return {
                value: props.value,
            };
        }
    };
    BaseForm.registerWidget = function (name, value) {
        widgets[name] = value;
    };
    BaseForm.prototype.componentDidUpdate = function (prevProps) {
        if (!isEqual(this.props.schema, prevProps.schema)) {
            this.setState({
                schema: parseSchema(this.props.schema),
            });
        }
    };
    BaseForm.prototype.render = function () {
        var _a = this.props, hideSubmitButton = _a.hideSubmitButton, submitButtonText = _a.submitButtonText, submitButtonProps = _a.submitButtonProps, secondaryButtonProps = _a.secondaryButtonProps, uiSchema = _a.uiSchema, validate = _a.validate, liveValidate = _a.liveValidate, noValidate = _a.noValidate, schema = _a.schema, value = _a.value, onFormChange = _a.onFormChange, onFormSubmit = _a.onFormSubmit, disabled = _a.disabled, props = __rest(_a, ["hideSubmitButton", "submitButtonText", "submitButtonProps", "secondaryButtonProps", "uiSchema", "validate", "liveValidate", "noValidate", "schema", "value", "onFormChange", "onFormSubmit", "disabled"]);
        var localSchema = omit(utils.disallowAdditionalProperties(this.state.schema), KEYWORD_BLACKLIST);
        return (React.createElement(FormWrapper, __assign({}, props, { onKeyDown: this.handleKeyUpDown, onKeyUp: this.handleKeyUpDown }),
            React.createElement(RsjfForm, { ref: this.formRef, disabled: disabled, liveValidate: liveValidate, noValidate: noValidate, validate: validate, showErrorList: false, schema: localSchema, formData: this.state.value, onSubmit: this.props.onFormSubmit, onChange: this.props.onFormChange, uiSchema: uiSchema, widgets: widgets, fields: fields, FieldTemplate: FieldTemplate, ObjectFieldTemplate: ObjectFieldTemplate, ArrayFieldTemplate: ArrayFieldTemplate },
                hideSubmitButton && React.createElement("span", null),
                !hideSubmitButton && (React.createElement(Button, __assign({ primary: true }, submitButtonProps, { type: "submit" }), submitButtonText || 'Submit')),
                !!secondaryButtonProps && (React.createElement(Button, __assign({ outline: true, secondary: true, ml: 3 }, secondaryButtonProps))))));
    };
    return BaseForm;
}(React.Component));
export var Form = BaseForm;
var templateObject_1;
